import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Avatar,
  Voice,
  UGCVideoMetadata,
  MediaSegment,
} from '../types';
import { debug } from "../../../utils/debugLogger";
import {
  fetchUGCVideoAvatars,
  fetchUGCVideoVoices
} from '../../../services/contentService';
import { MIN_SEGMENT_DURATION_SEC } from '../constants';

const DEFAULT_MEDIA_SEGMENT_DURATION_SEC = 5;
const TIMELINE_SNAP_SEC = 0.25;

function maxDurationForSegment(
  segments: MediaSegment[],
  mediaId: string,
  segmentTime: number,
  total: number
): number {
  let max = total - segmentTime;
  for (const other of segments) {
    if (other.media_id === mediaId) continue;
    if (other.time > segmentTime) max = Math.min(max, other.time - segmentTime);
  }
  return Math.max(MIN_SEGMENT_DURATION_SEC, max);
}
interface UseUGCVideoProps {
  customerId: string | null;
  idToken: string;
}



interface UseUGCVideoReturn {
  // Data
  avatars: Avatar[];
  customAvatars: Avatar[];
  voices: Voice[];

  // Selections
  selectedAvatar: Avatar | null;
  selectedVoice: Voice | null;
  /** Timeline segments (media_id + time + duration). Segments can be at any time; no reflow. */
  timelineSegments: MediaSegment[];
  /** Derived: media IDs for grid selection display. */
  selectedMediaIds: string[];

  // Loading states
  loadingAvatars: boolean;
  /** True once an avatar fetch has completed at least once (success or failure). */
  avatarsFetched: boolean;
  loadingVoices: boolean;

  // Error state
  error: string | null;

  // Selection functions
  selectAvatar: (avatar: Avatar) => void;
  selectVoice: (voice: Voice | null) => void;
  toggleMediaSelection: (mediaId: string, mediaType?: 'image' | 'video', insertAtTime?: number) => void;
  /** Update one segment's properties; no reflow (arbitrary placement allowed). */
  updateSegment: (mediaId: string, updates: Partial<MediaSegment>) => void;
  /** Remove a segment by media_id. */
  removeSegment: (mediaId: string) => void;
  /** Reorder segments by array index; times/durations unchanged. */
  reorderSegments: (fromIndex: number, toIndex: number) => void;
  /** Replace timeline segments (e.g. from parent); no reflow. */
  setTimelineSegments: (segments: MediaSegment[]) => void;
  /** Place or move a library image on the timeline at a given time (e.g. drag-drop from grid). */
  placeMediaOnTimelineAt: (mediaId: string, timeSec: number, timelineTotalSeconds: number) => void;

  // Utility functions
  clearSelections: () => void;
  hasValidSelections: () => boolean;
  getMetadata: (transcript: string) => UGCVideoMetadata | null;

  // Fetch functions
  fetchData: () => Promise<void>;
  fetchAvatars: () => Promise<void>;
  fetchVoices: () => Promise<void>;
}

export const useUGCVideo = ({
  customerId,
  idToken
}: UseUGCVideoProps): UseUGCVideoReturn => {
  // Data state
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [customAvatars, setCustomAvatars] = useState<Avatar[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);

  // Selection state
  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [timelineSegments, setTimelineSegments] = useState<MediaSegment[]>([]);

  // Loading states
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  /** True once an avatar fetch has actually completed (success or failure). Until then the
   *  avatar step shows skeletons, not the "please reload" empty state — otherwise it flashes
   *  the error before the first load even runs. */
  const [avatarsFetched, setAvatarsFetched] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  const fetchAvatars = useCallback(async () => {
    if (!customerId || !idToken) return;
    setError(null);
    setLoadingAvatars(true);
    try {
      const result = await fetchUGCVideoAvatars(customerId, idToken);
      setAvatars(result.avatars);
      setCustomAvatars(result.customAvatars);
    } catch (err: any) {
      // Don't fabricate mock avatars — that misleads the user into picking a fake presenter.
      // Clear the list so the avatar step shows its "please reload" empty state instead.
      debug.error('Failed to fetch avatars:', err);
      setAvatars([]);
      setCustomAvatars([]);
      setError(null);
    } finally {
      setLoadingAvatars(false);
      setAvatarsFetched(true);
    }
  }, [customerId, idToken]);

  const fetchVoices = useCallback(async () => {
    if (!customerId || !idToken) return;
    setError(null);
    setLoadingVoices(true);
    try {
      const voiceData = await fetchUGCVideoVoices(customerId, idToken);
      setVoices(voiceData);
    } catch (err: any) {
      debug.error('Failed to fetch voices:', err);
      setVoices([]);
      setError(err.message || 'Failed to fetch voices');
    } finally {
      setLoadingVoices(false);
    }
  }, [customerId, idToken]);

  const fetchData = useCallback(async () => {
    await fetchAvatars();
    await fetchVoices();
  }, [fetchAvatars, fetchVoices]);

  // Per-business-profile data isolation: avatars, voices (incl. the customer's own cloned
  // voices), and selections belong to ONE profile. The app doesn't remount on profile switch,
  // so when customerId changes we must drop the previous profile's data and refetch — this
  // also covers the initial load (auth hydrating after the panel opens).
  const fetchedForCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!customerId || !idToken) return;
    if (fetchedForCustomerRef.current === customerId) return;
    const isProfileSwitch = fetchedForCustomerRef.current !== null;
    fetchedForCustomerRef.current = customerId;
    if (isProfileSwitch) {
      setAvatars([]);
      setCustomAvatars([]);
      setVoices([]);
      setSelectedAvatar(null);
      setSelectedVoice(null);
      setAvatarsFetched(false);
    }
    void fetchData();
  }, [customerId, idToken, fetchData]);

  // Selection functions
  const selectAvatar = useCallback((avatar: Avatar) => {
    setSelectedAvatar(avatar);
  }, []);

  const selectVoice = useCallback((voice: Voice | null) => {
    setSelectedVoice(voice);
  }, []);

  const toggleMediaSelection = useCallback((mediaId: string, mediaType?: 'image' | 'video', insertAtTime?: number) => {
    setTimelineSegments(prev => {
      const existing = prev.find(s => s.media_id === mediaId);
      if (existing) {
        return prev.filter(s => s.media_id !== mediaId);
      }
      const nextStartTime = insertAtTime !== undefined
        ? insertAtTime
        : prev.length === 0
          ? 0
          : prev[prev.length - 1].time + prev[prev.length - 1].duration;
      return [...prev, {
        media_id: mediaId,
        time: nextStartTime,
        duration: DEFAULT_MEDIA_SEGMENT_DURATION_SEC,
        ...(mediaType ? { segment_type: mediaType } : {}),
        // Explicit fit: the editor previews unset fit as 'contain' but the backend renders
        // unset as 'cover' — images got cropped in the final video while looking fine in
        // the editor. Pin images to 'contain' (fully visible); videos keep 'cover'.
        ...(mediaType !== 'video' ? { fit: 'contain' as const } : {}),
      }];
    });
  }, []);

  const updateSegment = useCallback((mediaId: string, updates: Partial<MediaSegment>) => {
    setTimelineSegments(prev => {
      const idx = prev.findIndex(s => s.media_id === mediaId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  }, []);

  const removeSegment = useCallback((mediaId: string) => {
    setTimelineSegments(prev => prev.filter(s => s.media_id !== mediaId));
  }, []);

  const reorderSegments = useCallback((fromIndex: number, toIndex: number) => {
    setTimelineSegments(prev => {
      if (fromIndex < 0 || fromIndex >= prev.length || toIndex < 0 || toIndex >= prev.length) return prev;
      const reordered = [...prev];
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);
      return reordered;
    });
  }, []);

  const placeMediaOnTimelineAt = useCallback((mediaId: string, timeSec: number, timelineTotalSeconds: number) => {
    const total = Math.max(timelineTotalSeconds, MIN_SEGMENT_DURATION_SEC);
    let t = Math.max(0, Math.min(total - MIN_SEGMENT_DURATION_SEC, Math.round(timeSec / TIMELINE_SNAP_SEC) * TIMELINE_SNAP_SEC));

    setTimelineSegments((prev) => {
      const existingIdx = prev.findIndex((s) => s.media_id === mediaId);
      if (existingIdx >= 0) {
        const seg = prev[existingIdx];
        const maxD = maxDurationForSegment(prev, mediaId, t, total);
        const newDuration = Math.min(seg.duration, maxD);
        const next = [...prev];
        next[existingIdx] = {
          ...seg,
          time: t,
          duration: Math.max(MIN_SEGMENT_DURATION_SEC, newDuration),
        };
        return next;
      }
      const maxDur = maxDurationForSegment(prev, mediaId, t, total);
      const duration = Math.max(
        MIN_SEGMENT_DURATION_SEC,
        Math.min(DEFAULT_MEDIA_SEGMENT_DURATION_SEC, maxDur)
      );
      if (t + duration > total) {
        t = Math.max(0, total - duration);
      }
      // Library drops are images — same explicit 'contain' as toggleMediaSelection.
      return [...prev, { media_id: mediaId, time: t, duration, fit: 'contain' as const }];
    });
  }, []);

  const setTimelineSegmentsFromParent = useCallback((segments: MediaSegment[]) => {
    setTimelineSegments(segments);
  }, []);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedAvatar(null);
    setSelectedVoice(null);
    setTimelineSegments([]);
    setError(null);
  }, []);

  // Check if we have valid selections to send
  const hasValidSelections = useCallback(() => {
    return selectedAvatar !== null && selectedVoice !== null;
  }, [selectedAvatar, selectedVoice]);

  // Get metadata object for chat request
  const getMetadata = useCallback((transcript: string): UGCVideoMetadata | null => {
    if (!selectedAvatar || !selectedVoice) {
      return null;
    }

    return {
      avatar_id: selectedAvatar.avatar_id,
      voice_id: selectedVoice.voice_id,
      media_ids: timelineSegments.map(s => s.media_id),
      transcript,
    };
  }, [selectedAvatar, selectedVoice, timelineSegments]);

  const selectedMediaIds = timelineSegments.map(s => s.media_id);

  return {
    // Data
    avatars,
    customAvatars,
    voices,

    // Selections
    selectedAvatar,
    selectedVoice,
    timelineSegments,
    selectedMediaIds,

    // Loading states
    loadingAvatars,
    avatarsFetched,
    loadingVoices,

    // Error state
    error,

    // Selection functions
    selectAvatar,
    selectVoice,
    toggleMediaSelection,
    updateSegment,
    removeSegment,
    reorderSegments,
    setTimelineSegments: setTimelineSegmentsFromParent,
    placeMediaOnTimelineAt,

    // Utility functions
    clearSelections,
    hasValidSelections,
    getMetadata,

    // Fetch functions
    fetchData,
    fetchAvatars,
    fetchVoices,
  };
};

export default useUGCVideo;
